import {
  Avatar,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { MedicationOption } from 'Components/MedicationOption';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import {
  LeftArrowWhite,
  PenIcon,
  PurpleClose,
  PurpleVi,
  PurpleWarning,
} from 'Images';
import { useContext, useEffect, useState } from 'react';
import { OrderRegularMedicationsContext } from '../OrderRegularMedicationsContext';
import { sendEvent } from 'Utils/sendEvent';

interface INotAvailableMedication {
  outOfStockMedicationCode: number;
}

export const NotAvailableMedication = (props: INotAvailableMedication) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );

  const isDesktop = useIsDesktop();
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const [isPickupOption, setIsPickupOption] = useState(false);

  const {
    handleClose,
    nextStep,
    outOfStockMedications,
    prevStep,
    setOptionToOutOfStockMedication,
    steps,
    activeStep,
    expiredMedicationOptions: outOfStockMedicationOptions,
  } = useContext(OrderRegularMedicationsContext);
  const nextStepWrapper = () => {
    // if user have outofstock medicine, and he finished to select what to do.
    if (activeStep === steps.length - 2 && selfPurchaseMeds.length > 0) {
      setIsPickupOption(true);
    } else if (activeStep === steps.length - 1) {
      sendEvent('Medicine', 'ContinueButtonReplaceMedicine');
      nextStep();
    } else {
      setIsPickupOption(false);
      sendEvent('Medicine', 'ContinueButtonReplaceMedicine');
      nextStep();
    }
  };
  const prevStepWrapper = () => {
    sendEvent('Medicine', 'BackButtonReplaceMedicine');
    prevStep();
  };
  useEffect(() => {
    if (!outOfStockMedications.length) {
      nextStep();
    }
  }, [nextStep, outOfStockMedications]);

  const medication = outOfStockMedications.find(
    (outOfStockMedication) =>
      outOfStockMedication.medicatioN_CODE === props.outOfStockMedicationCode
  );

  if (medication === null || medication === undefined) return null;

  const selfPurchaseMeds = outOfStockMedications.filter(
    (med) =>
      med?.lackMedicineNote === 'אגיע בעצמי לאסוף את המרשם מהמחוז' ||
      med?.lackMedicineNote === 'שלחו לי מרשם בדואר'
  );

  const medOptions = outOfStockMedicationOptions.map((option, index) =>
    index === 1 ? null : (
      <MedicationOption
        isSelected={medication.lackMedicineNote === option.value}
        key={option.code}
        text={option.value}
        description={option.description}
        onClick={() => {
          setOptionToOutOfStockMedication(medication, option.value);
        }}
      />
    )
  );

  const selfOptions = outOfStockMedicationOptions.map((option, index) =>
    index === 1 || index === 2 ? (
      <MedicationOption
        isSelected={selfPurchaseMeds[0]?.lackMedicineNote === option.value}
        key={option.code}
        text={option.value}
        description={option.description}
        onClick={() => {
          selfPurchaseMeds.forEach((med) =>
            setOptionToOutOfStockMedication(med, option.value)
          );
        }}
      />
    ) : null
  );

  return (
    <>
      <DialogContent
        sx={{
          padding: isDesktop ? '32px' : '0 24px',
          backgroundColor: isDesktop ? '#FFF' : 'transparent',
          paddingBottom: isDesktop ? undefined : '180px',
          zIndex: 111,
        }}
      >
        <Paper
          sx={{
            backgroundColor: 'primary.shade_50',
            borderRadius: '16px',
            padding: isDesktop ? '24px' : '24px 6px',
            boxShadow: 'none',
            width: isDesktop ? undefined : '80vw',
          }}
        >
          <Stack alignItems='center'>
            <Grid
              container
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Typography
                variant='h3'
                component='h2'
                tabIndex={0}
                id='dialog-title'
              >
                {dictionary.requestRegularMedications}
              </Typography>

              {isDesktop && (
                <Avatar
                  role={'button'}
                  component={'button'}
                  alt={
                    dictionary.exitAndReturnToMedicationsAndPrescriptionsLobby
                  }
                  src={PurpleClose}
                  onClick={handleClose}
                  sx={{
                    cursor: 'pointer',
                    alignSelf: isDesktop ? '' : 'flex-start',
                    bottom: '45px',
                    left: '45px',
                    border: 'none',
                    background: 'transparent',
                    width: '40px',
                    height: '40px',
                    padding: 0,
                  }}
                />
              )}
            </Grid>
            <Divider
              aria-hidden='true'
              sx={{
                m: '12px 0 40px 0 ',
                width: '100%',
                borderColor: 'primary.shade_100',
              }}
            />
            <Typography variant='h5'>{dictionary.outOfStock}</Typography>

            {isPickupOption ? (
              <>
                <Typography variant='body2' m={'16px 0'}>
                  {dictionary.selfPurchaseMedications(selfPurchaseMeds.length)}
                </Typography>
                <Stack justifyContent={'center'} mb={4}>
                  {selfPurchaseMeds.map((med, i) => {
                    return (
                      <Stack
                        key={i}
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        mb={'5px'}
                      >
                        <Avatar
                          src={PurpleVi}
                          sx={{ height: '24px', width: '24px', mr: 1 }}
                          alt=''
                        />
                        <Typography variant='h5'>
                          {med.medicatioN_NAME}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </>
            ) : (
              <>
                <Typography variant='body2' mb={2} mt={1}>
                  {activeStep}
                  {dictionary.outOf}
                  {steps.length - 2}
                </Typography>
                <Stack
                  sx={{
                    backgroundColor: 'primary.shade_100',
                    border: '1px solid',
                    borderColor: 'primary.shade_200',
                    borderRadius: '12px',
                    maxWidth: '420px',
                    width: 'auto',
                    p: '24px',
                    mb: 4,
                  }}
                >
                  <Typography variant='h5' textAlign={'center'} mb={1}>
                    {medication.medicatioN_NAME}
                  </Typography>

                  <Stack flexDirection={'row'} alignItems={'center'}>
                    <Avatar
                      src={PurpleWarning}
                      alt={generalDictionary.warning}
                      sx={{ width: '24px', height: '24px', mr: 1 }}
                    />
                    <Typography variant='body2'>
                      {dictionary.weAreSorry}
                    </Typography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>

          <Stack
            alignItems='center'
            spacing={1.5}
            p={isDesktop ? undefined : '0 14px'}
          >
            {isPickupOption ? (
              <>
                <Typography variant='body2' mb={1}>
                  {dictionary.pickupChoose}
                </Typography>
                {selfOptions}
              </>
            ) : (
              <>
                <Typography variant='body2' mb={1}>
                  {dictionary.whatWouldYouLikeToDo}
                </Typography>
                {medOptions}
              </>
            )}
          </Stack>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={
          isDesktop
            ? {
                backgroundColor: '#fff',
                padding: '40px',
              }
            : {
                justifyContent: 'center',
                backgroundColor: '#fff',
                flexDirection: 'row',
                position: 'fixed',
                bottom: 0,
                width: '100%',
              }
        }
      >
        <Button
          sx={{
            p: isDesktop ? '8px 48px' : '4px 16px',
            width: isDesktop ? '230px' : '50%',
            fontSize: isDesktop ? '18px' : '16px',
            fontWeight: 700,
          }}
          startIcon={
            <Avatar
              src={PenIcon}
              alt=''
              sx={{
                width: '21px',
                height: '20px',
              }}
            />
          }
          variant='outlined'
          onClick={prevStepWrapper}
        >
          {generalDictionary.return}
        </Button>
        <Button
          sx={
            isDesktop
              ? {
                  padding: '8px 48px',
                  width: isDesktop ? '230px' : '50%',
                  fontSize: '18px',
                  fontWeight: 600,
                  ml: '32px !important',
                }
              : {
                  fontSize: '16px',
                  fontWeight: 600,
                  padding: '5px 24px',
                }
          }
          variant='contained'
          onClick={isPickupOption ? nextStep : nextStepWrapper}
          disabled={!Boolean(medication.lackMedicineNote)}
          endIcon={
            <Avatar
              src={LeftArrowWhite}
              alt=''
              sx={{
                width: '21px',
                height: '20px',
              }}
            />
          }
        >
          {generalDictionary.continue}
        </Button>
      </DialogActions>
    </>
  );
};
